export enum CancelReasons {
  SICKNESS = 'SICKNESS',
  OTHER = 'OTHER',
  CANCELLED_BY_PARTNER = 'CANCELLED_BY_PARTNER',
  CANCELLED_BY_STANDBY = 'CANCELLED_BY_STANDBY',
  CANCELLED_BY_VELOVE = 'CANCELLED_BY_VELOVE',
  CARE_OF_CHILDREN = 'CARE_OF_CHILDREN',
  PARENTAL_LEAVE = 'PARENTAL_LEAVE',
  INVALID_ABSENCE = 'INVALID_ABSENCE',
  SE_SICKNESS_DAY_1_SHIFT_1 = 'SE_SICKNESS_DAY_1_SHIFT_1',
  SE_SICKNESS_DAY_1_SHIFTS_2 = 'SE_SICKNESS_DAY_1_SHIFTS_2',
  SE_SICKNESS_DAY_1_SHIFTS_3 = 'SE_SICKNESS_DAY_1_SHIFTS_3',
  SE_SICKNESS_DAY_1_SHIFTS_4 = 'SE_SICKNESS_DAY_1_SHIFTS_4',
  SE_SICKNESS_DAY_1_SHIFTS_5_PLUS = 'SE_SICKNESS_DAY_1_SHIFTS_5_PLUS',
  SE_SICKNESS_DAY_2_PLUS = 'SE_SICKNESS_DAY_2_PLUS'
}

export enum Roles {
  RIDER = 'RIDER',
  COMMUNITY_MANAGER = 'COMMUNITY_MANAGER',
  SUPER_ADMIN = 'SUPER_ADMIN'
}

export enum UserStatus {
  /** "Normal" status (default) */
  AVAILABLE = 'AVAILABLE',
  /** Indicate a rider unavailable for some time but intending to come back later (unable to request new shifts) */
  BREAK = 'BREAK',
  /** Rider suspended for any reason by Community Managers (unable to request new shifts) */
  SUSPENDED = 'SUSPENDED',
  /** Rider having left Velove (unable to request new shifts) */
  QUIT = 'QUIT'
}

export enum ShiftType {
  /** The "regular" shift: a rider deliver packages for a given partner */
  DELIVERY = 'DELIVERY',
  /** A special shift: the manager helps riders to fulfill their shifts (and report problems etc.) */
  MANAGER = 'MANAGER',
  /** A special shift: loading manager helps riders with loading */
  LOADING = 'LOADING',
  /** Bike-fixing shift: not linked to a partner */
  MAINTENANCE = 'MAINTENANCE',
  /** A special shift : The instructor shows new riders how things work */
  INSTRUCTOR = 'INSTRUCTOR',
  /** A special shift : The buddy shows new riders using TRAINING shift */
  BUDDY = 'BUDDY',
  /** A special shift : Multiple training shifts are connected to one BUDDY shift */
  TRAINING = 'TRAINING',
  /** A shift for any kind of support task (mainly for Velove employees) */
  SUPPORT = 'SUPPORT',
  /** A shift for cleaning the Riders uniforms */
  CLEANING = 'CLEANING',
  /** A shift for sorting packages for the Riders */
  SORTER = 'SORTER',
  /** A shift for Linehaul packages for the Riders */
  LINEHAUL = 'LINEHAUL',
  /** A shift for different management tasks */
  MANAGEMENT_HR = 'MANAGEMENT_HR',
  MANAGEMENT_OPERATIONS = 'MANAGEMENT_OPERATIONS',
  MANAGEMENT_OTHER = 'MANAGEMENT_OTHER',
  /* A shift for Traffic Control */
  TRAFFIC_CONTROL_TEAM = 'TRAFFIC_CONTROL_TEAM',
  /** Placeholder shift type: cannot be scheduled, but can be used for adding reports on extra tasks the system does not specifically handle */
  OTHER = 'OTHER'
}

/**
 * Only for reports of shift type: "OTHER": list of categories a report belongs to
 */
export enum CostCategory {
  STANDBY_SHIFT = 'STANDBY_SHIFT',
  CANCELLED_BY_US = 'CANCELLED_BY_US',
  CANCELLED_BY_CUSTOMERS = 'CANCELLED_BY_CUSTOMERS',
  HELPING_COMMUNITY = 'HELPING_COMMUNITY',
  HELPING_OPERATIONS = 'HELPING_OPERATIONS',
  PROJECTS = 'PROJECTS',
  MARKETING = 'MARKETING',
  MOTIVATION_INCENTIVES = 'MOTIVATION_INCENTIVES',
  REFERRALS = 'REFERRALS',
  DIFF = 'DIFF',
  CLEANING = 'CLEANING',
  OTHER = 'OTHER'
}

// Base User type
export interface User {
  id: string;
  /**
   * Needed by the Financial Partners to identify compensation recipients
   * @warning highly sensitive PII, handle with extra care!
   */
  personalIdentificationNumber?: string | null;
  /**
   * Used to validate Authentication (match with Auth0 account), as well as destination for transactional emails
   * @warning highly sensitive PII, handle with extra care!
   */
  email: string;
  /** @warning sensitive PI */
  firstName: string;
  /** @warning sensitive PI */
  lastName: string;
  /**
   * Full name (aggregated from firstName and lastName)
   * @warning sensitive PI
   */
  name: string;
  /**
   * Currently only meant to be displayed for internal communication purposes
   * @warning highly sensitive PII, handle with extra care!
   */
  phoneNumber?: string;
  onboardedAt?: Date | null;
  onboardingSource?: string | null;
  certifiedPartners: Partner[];
  canBeShiftManager?: boolean;
  canBeLoadingManager?: boolean;
  canDoMaintenance?: boolean;
  canBeInstructor?: boolean;
  canBeBuddy?: boolean;
  status?: UserStatus;
  locations?: Location[];
  maxWorkDaysPerWeek?: number;
}

/** Just for the convenience when needed to specify locations */
export interface UserWithLocations extends User {
  locations: Location[];
}

export interface UserDetail extends User {
  weeklyShifts?: Omit<Shift, 'rider'>[];
  locations: Location[];
}

/** @deprecated use 'User' directly instead */
export interface Rider extends User {
  /** @deprecated this contextual property has been moved to the Shift interface instead */
  numberOfPreviousShiftsForPartner?: number;
}

export interface MatchingRider extends Rider {
  weeklyShifts?: Omit<Shift, 'rider'>[];
}

// At the moment, Omit<Rider, 'certifiedPartners'> is the same as User, but Rider might evolve later on
export interface RequestingRider<TShiftType extends Shift = Shift> extends Omit<Rider, 'certifiedPartners'> {
  rating?: number;
  weeklyShifts: Omit<TShiftType, 'rider'>[];
}

// Business Logic entities
export interface Partner {
  id: string;
  name: string;
  countryCode?: string;
}

export interface Location {
  id: string;
  name: string;
}

export interface LocationDetail extends Location {
  hubs: Hub[];
}

export interface Hub {
  id: string;
  name: string;
  address: string;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
  timeZone: string;
}

export interface Bike {
  id: string;
  name: string;
}

export interface AvailableShift<TDateType = Date | string> {
  id: string;
  publishedAt?: TDateType;
  startAt: TDateType;
  endAt: TDateType;
  type: ShiftType;
  partner?: Partner;
  hub: Hub;
  numberOfRiders: number;
}

/**
 * Interface describing someone who approved a shift (based on "managerId" internally)
 */
export interface ShiftApprover {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ShiftRequest<TDateType = Date | string> {
  id: string;
  approvedAt?: TDateType | null;
}

export interface AvailableShiftForRider<TDateType = Date | string> extends AvailableShift<TDateType> {
  isFullyBooked?: boolean;
  /** @deprecated use 'shift' property instead */
  isAlreadyRequested: boolean;
  shift?: ShiftRequest<TDateType>;
}

export interface AvailableShiftWithAssignments<TDateType = Date | string> extends AvailableShift<TDateType> {
  isFullyBooked?: boolean;
  numberOfRequests: number;
  numberOfAssignedRiders: number;
}

// Allow to override the availableShift type
export interface Shift<TAvailableShiftType extends AvailableShift = AvailableShift, TDateType = Date | string> {
  id: string;
  /** Timestamp of the approval (if defined, the shift is "approved") */
  approvedAt?: TDateType | null;
  /** Informations about the Community Manager having approved the shift (if undefined, it is either unknown, or from an automatic assignment) */
  approvedBy?: ShiftApprover;
  /** Timestamp of the cancelation (if defined, the shift is "canceled") */
  canceledAt?: TDateType | null;
  /** Reason for canceling (enum) */
  cancelReason?: CancelReasons;
  /** Description (free text) about the cancelation reason */
  cancelDescription?: string;
  /** field not used at the moment */
  comment?: string;
  availableShift: TAvailableShiftType;
  rider?: Rider;
  bike?: Bike;
  /**
   * @desc total number of *previous* shifts (of type Delivery) of the rider (all partners)
   * @note contextual property, defined only in certain contexts
   */
  numberOfPreviousShifts?: number;
  /**
   * @desc total number of *previous* shifts (of type Delivery) of the rider with the same partner (will be 0 if there is no partner, like Maintenance shifts etc.)
   * @note contextual property, defined only in certain contexts
   */
  numberOfPreviousShiftsForPartner?: number;
}

export interface ShiftWithRequestingRider<
  TAvailableShiftType extends AvailableShift = AvailableShift,
  TDateType = Date | string
> extends Omit<Shift<TAvailableShiftType, TDateType>, 'availableShift' | 'rider'> {
  rider: RequestingRider<Shift<TAvailableShiftType>>;
}

export interface ShiftDetail<TAvailableShiftType extends AvailableShift = AvailableShift, TDateType = Date | string>
  extends Shift<TAvailableShiftType, TDateType> {
  /** contains the list of shifts to manage (indirect relationship based on hub & date) for Shift Manager or Buddy shifts */
  shiftsToManage?: Shift<TAvailableShiftType, TDateType>[];
  /** contains the list of Shift Manager shifts (indirect relationship based on hub & date) */
  managerShifts?: Shift<TAvailableShiftType, TDateType>[];
  waitingListCount?: number;
}

export interface Report<TShiftType extends Shift = Shift, TDateType = Date | string> {
  id: string;
  /** @deprecated unused early feature, should be safely ignored */
  scanningStartedAt?: TDateType | null;
  /** @deprecated unused early feature, should be safely ignored */
  scanningStoppedAt?: TDateType | null;
  /**
   * "Start Route" timestamp
   *
   * Default to null (until report is submitted, or if not applicable e.g. on non delivery shifts)
   */
  deliveryStartedAt?: TDateType | null;
  /**
   * "End Route" timestamp
   *
   * Default to null (until report is submitted, or if not applicable e.g. on non delivery shifts)
   */
  deliveryStoppedAt?: TDateType | null;
  /**
   * Number of recipients delivered by a rider (wether the customer was home or not)
   *
   * Default to null (until report is submitted, or if not applicable e.g. on non delivery shifts)
   */
  numberOfDeliveredRecipients?: number | null;
  /**
   * Number of recipients delivered by a rider, but for which at least one parcel was missing (that would mean the partner failed to provide at least one parcel for that customer)
   *
   * Default to null (until report is submitted, or if not applicable e.g. on non delivery shifts)
   */
  numberOfMissingParcels?: number | null;
  /**
   * Number of recipients not delivered after the shift, in case the rider did not have the time to finish delivering them all
   *
   * Default to null (until report is submitted, or if not applicable e.g. on non delivery shifts)
   */
  numberOfRemainingRecipients?: number | null;
  /**
   * Feedback provided by the rider after their shift
   */
  comment?: string | null;
  /** @deprecated use "didNoticeDangerousSituations" instead */
  didNoticeAccidents?: boolean | null;
  /** @deprecated use "dangerousSituationsReport" instead */
  accidentReport?: string | null;
  /**
   * Did the rider notice dangerous situations / situations which could have become dangerous during their shift? (null meaning they did not report it)
   */
  didNoticeDangerousSituations?: boolean | null;
  /**
   * Did the rider notice dangerous situations / situations which could have become dangerous during their shift? (null meaning they did not report it)
   */
  dangerousSituationsReport?: string | null;
  /**
   * Was the rider involved in accident
   */
  involvedInAnAccident?: boolean | null;
  /**
   * When the rider was involved in accident here goes description
   */
  involvedInAnAccidentReport?: string | null;
  /**
   * Duration (in minutes) of extra time during which the rider has worked but not delivering (other specific tasks for Velove, also used for non-delivery shifts)
   */
  extraTime?: number;
  /**
   * Flag to highlight if container from partner was delivered after shift start
   */
  isContainerDelayed?: boolean;
  /**
   * Duration (in minutes) defining how long the rider was waiting for the container(s) before starting a shift.
   *
   * Default is 0 (if no matching delayed containers was reported, or if the rider did not claim a delay)
   */
  delayDuration?: number;
  /**
   * Flag to highlight if there are any despcrepancies in the report such as unreasonably short shift
   */
  isDescrepancy?: boolean;
  /**
   * Score, in recipients / hour
   * Default to null (until report is rated, or if it cannot be calculated for any reason)
   */
  productivityScore?: number | null;
  /**
   * Decimal number on interval [0, 10]
   * Default to null (until report is rated, or if it cannot be calculated for any reason)
   *
   * @alias usually known as "Route score"
   */
  productivityRate?: number | null;
  /**
   * Average reference score used to calculate the productivityRate, in recipients / hour
   * Default to null (until report is rated, or if it cannot be calculated for any reason)
   *
   * @alias usually known as "Average productivity"
   */
  referenceScore?: number | null;
  /**
   * Number of reports counted to calculate the referenceScore (undefined until report is rated)
   */
  referenceWeight?: number | null;
  /**
   * Fixed compensation applied to compensate for preparation time (may not apply to all markets / partners / revisions)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  fixedCompensationAmount?: number;
  /**
   * Hourly rate (applied to the *route base compensation only* (before equalizer)
   * (Depending on the revision, can be based on productivity or not)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  routeCompensationHourlyRate?: number | null;
  /**
   * Base compensation for the route part (before equalizer is applied), also needed to calculate pension etc.
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  routeBaseCompensationAmount?: number;
  /**
   * Additional compensation for the route part (to reward productive riders and compensate their lower route duration)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  routeEqualizerCompensationAmount?: number;
  /**
   * Bonus amount given to highly productive reports (may not be for all markets / partners)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  routeProductivityBonusCompensationAmount?: number;
  /**
   * Total compensation for the route (base + equalizer + productivity bonus)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  routeCompensationAmount?: number;
  /**
   * Hourly rate for extra compensation (depends on the market / revision)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  extraCompensationHourlyRate?: number | null;
  /**
   * Compensation for extra working hours (& main compensation for non-delivery shifts)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  extraCompensationAmount?: number;
  /**
   * Compensation for delayed containers before the route (if riders claiming delay match Shift Managers reported delayed containers)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  delayCompensationAmount?: number;
  /**
   * Compensation for sorting parcels before the route (for some partners & locations)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  parcelsSortingCompensationAmount?: number;
  /**
   * Pre-total (fixed + route + delayed containers + extra) compensation amount (before applying legal compensations such as pension etc.)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  rawCompensationAmount?: number;
  /**
   * Compensation for unsocial working hours (e.g. "night work", depends on local legislations and revision)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  legalWorkingHoursCompensationAmount?: number;
  /**
   * Compensation for "holidays" (e.g. "semesterersättning", depends on local legislations and revision)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  legalHolidaysCompensationAmount?: number;
  /**
   * Compensation for pension (appliance depends on local legislations and revision)
   *
   * Default to 0 (until report compensation is calculated, or if not applicable)
   */
  legalPensionCompensationAmount?: number;
  /**
   * Total amount (fixed + route + delayed containers + extra + legal etc.) for the shift (using the model it was calculated with), stored to avoid recalculating it
   *
   * Default to 0 (until report compensation is calculated)
   */
  totalCompensationAmount?: number;
  /**
   * The ISO_4217 currency code used for the compensation (based on the hub's country)
   *
   * null until report compensation is calculated
   */
  currencyCode?: string | null;
  /**
   * Internal revision (version) indicating which compensation model was used to calculate compensation
   *
   * null until report compensation is calculated or if unknown
   */
  compensationRevision?: string;
  /**
   * Only for report of shift type "Other": a category to group costs in Data Studio
   */
  costCategory?: string | null;
  /**
   * Only for report of shift type "Other": when "costCategory" value is "OTHER", indicates the cost category
   */
  costCategoryDetail?: string | null;
  /** Budbee specific information: Budbee internal route reference (imported from Budbee system) */
  budbeeRouteId?: string | null;
  /** Budbee specific information: number of performed stops (imported from Budbee system) */
  budbeeNumberOfStops?: number | null;
  /** Budbee specific information: number of 5* ratings received from customers (imported from Budbee system) */
  budbeeNumberOfTopRatings?: number | null;
  /** Budbee specific information: number of heavy additional parcels per stop giving compensation bonus (imported from Budbee system) */
  budbeeNumberOfMultipleParcels?: number | null;
  /** Budbee specific information: number of heavy parcels giving compensation bonus (imported from Budbee system) */
  budbeeNumberOfHeavyParcels?: number | null;
  /** Budbee specific information: estimated working hours (imported from Budbee system) */
  budbeeEstimatedHours?: number | null;
  /** Budbee specific information: estimated unsocial hours (imported from Budbee system) */
  budbeeEstimatedSCEWHours?: number | null;
  /**
   * The Shift associated to the report.
   *
   * This generic type that can be overriden in favor of more specific "Shift" types if needed
   */
  shift: TShiftType;
}

/** Report with additional informations about previous shifts */
export interface ReportWithPreviousScores<TShiftType extends Shift = Shift, TDateType = Date | string>
  extends Report<TShiftType, TDateType> {
  /** "productivityRate" of the previous shift for the same partner (and rider), could be null in case of the first shift, or if the query does not specifically fetch it (quite expensive information) */
  previousProductivityRate?: number | null;
  /** average "productivityScore" of all riders for the same {date, partner & location} */
  dailyAverageProductivityScore?: number | null;
  /** average "productivityRate" of all riders for the same {date, partner & location} */
  dailyAverageProductivityRate?: number | null;
}

/**
 * Interface describing one row in the Compensation Specifications template
 */
export interface Payslip<TDateType = Date | string> {
  /**
   * Needed by the Financial Partners to identify compensation recipients
   * @warning highly sensitive PII, handle with extra care!
   */
  riderPersonalIdentificationNumber: string;
  /** internal rider ID (in case there is a need to identify them) */
  riderId: string;
  /** @warning sensitive PI */
  riderName: string;
  /** total compensation for all shifts of the rider */
  riderTotal: number;
  /** official shift type */
  availableShiftType: ShiftType;
  /** official shift start */
  availableShiftStartAt: TDateType;
  /** partner name (for delivery shifts) */
  partnerName: string;
  /** reported start time (for delivery shifts) */
  routeStartAt?: TDateType | null;
  /** reported end time (for delivery shifts) */
  routeEndAt?: TDateType | null;
  /** @deprecated use "fixedCompensationAmount" instead */
  preparationCompensation: number;
  /** fixed compensation (if applicable) */
  fixedCompensationAmount: number;
  /** extra time given as a compensation bonus, or worked hours for non delivery shifts */
  extraTime: number;
  /** @deprecated use "extraCompensationAmount" instead */
  extraCompensation: number;
  /** compensation for extra time (if applicable) */
  extraCompensationAmount: number;
  /** compensation for delayed containers (if applicable) */
  delayCompensationAmount: number;
  /** compensation for sorting parcels (if applicable) */
  parcelsSortingCompensationAmount: number;
  /** aka "Route Score" (& "productivityRate") */
  routeRating: number;
  /** @deprecated use "routeCompensationHourlyRate" instead */
  routeHourlyRate: number;
  /** hourly rate (based on Route Score, if applicable) */
  routeCompensationHourlyRate: number;
  /** Productivity compensation algorithm result */
  routeEqualizerCompensationAmount: number;
  /** will only be defined once report is submitted */
  routeProductivityBonusCompensationAmount: number;
  /** @deprecated use "routeCompensationAmount" instead */
  routeCompensation: number;
  /** compensation for the delivery time (if applicable) */
  routeCompensationAmount: number;
  /** @deprecated use "totalCompensationAmount" instead */
  shiftTotal: number;
  /** Unsocial hours */
  legalWorkingHoursCompensationAmount: number;
  /** Holiday compensation */
  legalHolidaysCompensationAmount: number;
  /** Pension compensation, for peoplpe over 24 y.o */
  legalPensionCompensationAmount: number;
  /** total compensation for the shift */
  totalCompensationAmount: number;
  /** ISO_4217 currency code */
  currencyCode: string;
  /** Version of the compensation system */
  compensationRevision?: string;
}

export interface UserRatingDetail {
  /** current rating of the current rider (aggregated: all factors combined) */
  currentRating: number | null;
  /** The average rating for riders sharing the same location (at least one hub) */
  localAverageRating: number | null;
  /** The number of *rated* riders in the same location (including the current one - if rated -). Value should be >= 0) */
  localNumberOfRatedRiders: number;
  /**
   * The ranking of the user compared to the other riders in the same location
   *
   * The minimum is 1, for the highest rating.
   * The maximum should be equal to localNumberOfRatedRiders above.
   *
   * It can also be null if the currentRating is null
   */
  localRanking: number | null;
  /**
   * The percentile of the currentRating compared to the other riders in the same location
   * @deprecated this is not entirely relevant. Do not use
   */
  localPercentile: number | null;
  /** In-depth informations about the productivity factor (mainly to prepare for later when we add new factors) */
  productivity: {
    /** productivity factor score: can be null if the rider have not had enough shifts */
    productivityScore: number | null;
    /** The reports used to calculate the productivity factor of the rating */
    reports: Report[];
  };
}

/**
 * History record: for a given date, provides the productivityScore and productivityRate of a given rider, as well as the average productivityScore and productivityRate of all riders for a given partner
 */
export interface ReportScoresHistoryRecord<TDateType = Date | string> {
  date: TDateType;
  partner: Partner;
  productivityScore?: number | null;
  productivityRate?: number | null;
  /** Daily average productivityScore of all riders for the same partner & location (can be null in case none worked that day) */
  averageProductivityScore?: number | null;
  /** Daily average productivityRate [0, 10] of all riders for the same partner & location (can be null in case none worked that day) */
  averageProductivityRate?: number | null;
}

export interface ReportScoresHistory<TDateType = Date | string> {
  user: User;
  history: ReportScoresHistoryRecord<TDateType>[];
}

export interface DelayedContainer<TDateType = Date | string> {
  id: string;
  expectedTime: TDateType;
  timeOfArrival: TDateType;
  numberOfContainers: number;
  partner: Partner | null;
  hub: Hub | null;
}
